import React, { useState, useMemo } from 'react'
import { Input, Button, Modal } from 'antd'
import {
  ThunderboltFilled,
  CheckCircleFilled,
  ArrowLeftOutlined,
} from '@ant-design/icons'
import {
  MATERIAL_BY_CODE,
  isProductInAnyMatch,
  PLACEHOLDER_IMG,
} from './reemplazantesConstants'

const CONFETTI_COLORS = [
  '#235a97',
  '#2c6196',
  '#5b89b8',
  '#22c55e',
  '#f59e0b',
  '#38bdf8',
]

// Afinidad visual determinista a partir del id (demo, sin score real)
const getAffinity = (id) => {
  const base = String(id)
    .split('')
    .reduce((acc, ch) => acc + ch.charCodeAt(0), 0)
  return 72 + (base % 27) // 72% – 98%
}

const getAffinityNivel = (pct) => {
  if (pct >= 90) return 'high'
  if (pct >= 80) return 'mid'
  return 'low'
}

export const ProductCard = ({
  product,
  isReference,
  isSelected,
  isMatched,
  isCustom,
  index = 0,
  onClick,
}) => {
  const classNames = [
    'reemplazantes-product-card',
    isReference && 'reemplazantes-product-card--reference',
    isSelected && 'reemplazantes-product-card--selected',
    isMatched && 'reemplazantes-product-card--matched',
    isCustom && 'reemplazantes-product-card--custom',
    onClick && 'reemplazantes-product-card--selectable',
  ]
    .filter(Boolean)
    .join(' ')

  const CardTag = onClick ? 'button' : 'article'
  const affinity = !isReference
    ? typeof product.similarityPct === 'number'
      ? product.similarityPct
      : getAffinity(product.id)
    : null
  const nivel = affinity ? getAffinityNivel(affinity) : 'mid'

  return (
    <CardTag
      type={onClick ? 'button' : undefined}
      className={classNames}
      style={{ '--card-index': index % 8 }}
      onClick={onClick}
    >
      <span className="reemplazantes-product-card-shine" aria-hidden />
      {isReference && (
        <span className="reemplazantes-product-card-badge">Referencia</span>
      )}
      {isCustom && !isMatched && (
        <span className="reemplazantes-product-card-badge reemplazantes-product-card-badge--added">
          Agregado
        </span>
      )}
      {isMatched && (
        <span className="reemplazantes-product-card-badge reemplazantes-product-card-badge--match">
          <CheckCircleFilled /> Match
        </span>
      )}
      <div className="reemplazantes-product-card-image-wrap">
        <img
          src={product.imagen || PLACEHOLDER_IMG}
          alt={product.nombre}
          className="reemplazantes-product-card-image"
          onError={(event) => {
            event.currentTarget.src = PLACEHOLDER_IMG
          }}
        />
        {affinity != null && !isMatched && (
          <span
            className={`reemplazantes-product-card-affinity reemplazantes-product-card-affinity--${nivel}`}
            title="Afinidad estimada"
          >
            <ThunderboltFilled /> {affinity}%
          </span>
        )}
      </div>
      <div className="reemplazantes-product-card-body">
        <p>
          <strong>Nombre:</strong> {product.nombre}
        </p>
        <p>
          <strong>Marca:</strong> {product.marca}
        </p>
        <p>
          <strong>Categoría:</strong> {product.categoria}
        </p>
        <p>
          <strong>Costo:</strong> {product.costo}
        </p>
      </div>
    </CardTag>
  )
}

export const MatchCelebration = ({ leftProduct, rightProduct }) => {
  const confetti = useMemo(
    () =>
      Array.from({ length: 36 }, (_, i) => ({
        id: `confetti-${i}`,
        left: `${(i * 33 + 7) % 100}%`,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        delay: `${(i % 12) * 0.06}s`,
        duration: `${1.1 + ((i * 7) % 9) / 10}s`,
        rotate: `${(i * 47) % 360}deg`,
      })),
    [],
  )
  const hearts = useMemo(
    () =>
      Array.from({ length: 10 }, (_, i) => ({
        id: `heart-${i}`,
        left: `${8 + ((i * 23 + 5) % 84)}%`,
        delay: `${0.1 + (i % 10) * 0.12}s`,
        size: `${16 + ((i * 5) % 20)}px`,
      })),
    [],
  )

  return (
    <div className="reem-match-celebration" role="alert" aria-label="¡Match!">
      <div className="reem-match-celebration__backdrop" />

      <div className="reem-match-celebration__confetti" aria-hidden>
        {confetti.map((c) => (
          <span
            key={c.id}
            className="reem-mc-confetti"
            style={{
              left: c.left,
              background: c.color,
              animationDelay: c.delay,
              animationDuration: c.duration,
              transform: `rotate(${c.rotate})`,
            }}
          />
        ))}
      </div>

      {hearts.map((h) => (
        <span
          key={h.id}
          className="reem-mc-heart"
          aria-hidden
          style={{ left: h.left, animationDelay: h.delay, fontSize: h.size }}
        >
          <ThunderboltFilled />
        </span>
      ))}

      <div className="reem-match-celebration__content">
        <div className="reem-match-celebration__cards">
          <div className="reem-mc-card reem-mc-card--left">
            <img
              src={leftProduct.imagen || PLACEHOLDER_IMG}
              alt={leftProduct.nombre}
              onError={(event) => {
                event.currentTarget.src = PLACEHOLDER_IMG
              }}
            />
            <span className="reem-mc-card__code">{leftProduct.nombre}</span>
          </div>

          <div className="reem-mc-heartburst">
            <ThunderboltFilled />
          </div>

          <div className="reem-mc-card reem-mc-card--right">
            <img
              src={rightProduct.imagen || PLACEHOLDER_IMG}
              alt={rightProduct.nombre}
              onError={(event) => {
                event.currentTarget.src = PLACEHOLDER_IMG
              }}
            />
            <span className="reem-mc-card__code">{rightProduct.nombre}</span>
          </div>
        </div>

        <div className="reem-match-celebration__title">¡MATCH!</div>
        <div className="reem-match-celebration__subtitle">
          <ThunderboltFilled /> Reemplazante vinculado
        </div>
      </div>
    </div>
  )
}

const formatMedidas = (medidas) =>
  medidas && (medidas.A != null || medidas.B != null || medidas.C != null)
    ? `A: ${medidas.A ?? '—'} cm · B: ${medidas.B ?? '—'} cm · C: ${
        medidas.C ?? '—'
      } cm`
    : '—'

const COMPARISON_ROWS = [
  { key: 'id', label: 'ID', get: (p) => p.id },
  { key: 'nombre', label: 'Nombre', get: (p) => p.nombre },
  { key: 'marca', label: 'Marca', get: (p) => p.marca },
  { key: 'categoria', label: 'Categoría', get: (p) => p.categoria },
  { key: 'subcategoria', label: 'Subcategoría', get: (p) => p.subcategoria },
  { key: 'costo', label: 'Costo', get: (p) => p.costo },
  { key: 'proveedor', label: 'Proveedor', get: (p) => p.proveedor },
  { key: 'medidas', label: 'Medidas', get: (p) => formatMedidas(p.medidas) },
]

const normalizeValue = (value) =>
  value === null || value === undefined
    ? ''
    : String(value).trim().toLowerCase()

// Campos cuyo valor coincide en ambos materiales (se resaltan)
const getMatchedFields = (left, right) => {
  const matched = new Set()
  COMPARISON_ROWS.forEach(({ key, get }) => {
    if (key === 'id') return
    const a = normalizeValue(get(left))
    const b = normalizeValue(get(right))
    if (a && a === b) matched.add(key)
  })
  return matched
}

export const ComparisonDetailCard = ({
  product,
  title,
  badge,
  matchedFields,
}) => (
  <article className="reemplazantes-comparison-card">
    <header className="reemplazantes-comparison-card-header">
      <h4 className="reemplazantes-comparison-card-title">{title}</h4>
      {badge && (
        <span className="reemplazantes-comparison-card-badge">{badge}</span>
      )}
    </header>
    <div className="reemplazantes-comparison-card-image-wrap">
      <img
        src={product.imagen || PLACEHOLDER_IMG}
        alt={product.nombre}
        className="reemplazantes-comparison-card-image"
        onError={(event) => {
          event.currentTarget.src = PLACEHOLDER_IMG
        }}
      />
    </div>
    <dl className="reemplazantes-comparison-card-details">
      {COMPARISON_ROWS.map(({ key, label, get }) => {
        const isMatch = matchedFields?.has(key)
        return (
          <div
            key={key}
            className={`reemplazantes-comparison-row${
              isMatch ? ' reemplazantes-comparison-row--match' : ''
            }`}
          >
            <dt>{label}</dt>
            <dd>
              {get(product)}
              {isMatch && (
                <CheckCircleFilled className="reemplazantes-comparison-row-check" />
              )}
            </dd>
          </div>
        )
      })}
    </dl>
  </article>
)

export const MatchesSummary = ({ title, pairs }) => {
  if (!pairs.length) return null

  return (
    <div className="reemplazantes-matches-summary">
      <p className="reemplazantes-matches-summary-title">{title}</p>
      <ul className="reemplazantes-matches-summary-list">
        {pairs.map((pair) => (
          <li key={pair.key}>{pair.label}</li>
        ))}
      </ul>
    </div>
  )
}

export const ComparisonOverlay = ({
  leftProduct,
  rightProduct,
  leftTitle,
  rightTitle,
  leftBadge,
  rightBadge,
  matchLabel,
  unmatchLabel,
  isMatched,
  onClose,
  onMatchToggle,
}) => {
  const [celebrating, setCelebrating] = useState(false)

  const matchedFields = useMemo(
    () => getMatchedFields(leftProduct, rightProduct),
    [leftProduct, rightProduct],
  )

  const matchPct =
    typeof rightProduct.similarityPct === 'number'
      ? rightProduct.similarityPct
      : getAffinity(rightProduct.id)

  const handleMatchClick = () => {
    // Al deshacer un match, no hay celebración
    if (isMatched) {
      onMatchToggle()
      return
    }
    setCelebrating(true)
    setTimeout(() => onMatchToggle(), 2200)
  }

  return (
    <div
      className="reemplazantes-comparison-overlay"
      role="dialog"
      aria-label="Comparación de materiales"
    >
      <button
        type="button"
        className="reemplazantes-comparison-backdrop"
        aria-label="Cerrar comparación"
        onClick={celebrating ? undefined : onClose}
      />

      <div className="reemplazantes-comparison-panel">
        <div className="reemplazantes-comparison-topbar">
          <button
            type="button"
            className="reemplazantes-comparison-back"
            onClick={onClose}
            disabled={celebrating}
          >
            <ArrowLeftOutlined /> Volver
          </button>
          <span className="reemplazantes-comparison-heading">
            Comparación de materiales
          </span>
          {isMatched && (
            <span className="reemplazantes-comparison-matched-chip">
              <CheckCircleFilled /> Match activo
            </span>
          )}
        </div>

        <div className="reemplazantes-comparison-cards">
          <ComparisonDetailCard
            product={leftProduct}
            title={leftTitle}
            badge={leftBadge}
            matchedFields={matchedFields}
          />
          <div className="reemplazantes-comparison-divider" aria-hidden="true">
            <span className="reemplazantes-comparison-vs">
              <span className="reemplazantes-comparison-vs-pct">{matchPct}%</span>
              <span className="reemplazantes-comparison-vs-label">match</span>
            </span>
          </div>
          <ComparisonDetailCard
            product={rightProduct}
            title={rightTitle}
            badge={rightBadge}
            matchedFields={matchedFields}
          />
        </div>

        <div className="reemplazantes-comparison-actions">
          <Button
            type={isMatched ? 'default' : 'primary'}
            danger={isMatched}
            size="large"
            className={`reemplazantes-match-button${
              isMatched ? '' : ' reemplazantes-match-button--pulse'
            }`}
            onClick={handleMatchClick}
            disabled={celebrating}
            icon={isMatched ? undefined : <ThunderboltFilled />}
          >
            {isMatched ? unmatchLabel ?? 'Dismatch' : matchLabel ?? 'Match'}
          </Button>
        </div>
      </div>

      {celebrating && (
        <MatchCelebration
          leftProduct={leftProduct}
          rightProduct={rightProduct}
        />
      )}
    </div>
  )
}

export const AddMaterialModal = ({
  open,
  customProducts,
  onClose,
  onConfirm,
}) => {
  const [materialCodeInput, setMaterialCodeInput] = useState('')
  const [addMaterialError, setAddMaterialError] = useState(null)
  const [pendingAddProduct, setPendingAddProduct] = useState(null)

  const resetForm = () => {
    setMaterialCodeInput('')
    setAddMaterialError(null)
    setPendingAddProduct(null)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleValidateMaterialCode = () => {
    const code = materialCodeInput.trim()

    if (!MATERIAL_BY_CODE[code]) {
      setPendingAddProduct(null)
      setAddMaterialError('Material no encontrado')
      return
    }

    const alreadyAdded = customProducts.some(
      (product) => product.id === MATERIAL_BY_CODE[code].id,
    )

    if (alreadyAdded) {
      setPendingAddProduct(null)
      setAddMaterialError('Este material ya fue agregado')
      return
    }

    setAddMaterialError(null)
    setPendingAddProduct(MATERIAL_BY_CODE[code])
  }

  const handleConfirm = () => {
    if (!pendingAddProduct) return
    onConfirm(pendingAddProduct)
    handleClose()
  }

  return (
    <Modal
      title={pendingAddProduct ? 'Confirmar material' : 'Agregar material'}
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      afterClose={resetForm}
    >
      {!pendingAddProduct ? (
        <div className="reemplazantes-add-material-form">
          <label
            className="reemplazantes-add-material-label"
            htmlFor="material-code-input"
          >
            Código de material
          </label>
          <Input
            id="material-code-input"
            placeholder="Ingresa el código..."
            value={materialCodeInput}
            onChange={(event) => {
              setMaterialCodeInput(event.target.value)
              setAddMaterialError(null)
            }}
            onPressEnter={handleValidateMaterialCode}
          />
          {addMaterialError && (
            <p className="reemplazantes-add-material-error" role="alert">
              {addMaterialError}
            </p>
          )}
          <div className="reemplazantes-add-material-actions">
            <Button onClick={handleClose}>Cancelar</Button>
            <Button type="primary" onClick={handleValidateMaterialCode}>
              Buscar
            </Button>
          </div>
        </div>
      ) : (
        <div className="reemplazantes-add-material-confirm">
          <p className="reemplazantes-add-material-confirm-text">
            ¿Deseas agregar este material a los reemplazantes?
          </p>
          <dl className="reemplazantes-add-material-details">
            <div>
              <dt>Código</dt>
              <dd>{pendingAddProduct.codigo}</dd>
            </div>
            <div>
              <dt>Nombre</dt>
              <dd>{pendingAddProduct.nombre}</dd>
            </div>
            <div>
              <dt>Marca</dt>
              <dd>{pendingAddProduct.marca}</dd>
            </div>
            <div>
              <dt>Categoría</dt>
              <dd>{pendingAddProduct.categoria}</dd>
            </div>
            <div>
              <dt>Subcategoría</dt>
              <dd>{pendingAddProduct.subcategoria}</dd>
            </div>
            <div>
              <dt>Costo</dt>
              <dd>{pendingAddProduct.costo}</dd>
            </div>
            <div>
              <dt>Proveedor</dt>
              <dd>{pendingAddProduct.proveedor}</dd>
            </div>
            <div>
              <dt>Medidas</dt>
              <dd>
                A: {pendingAddProduct.medidas.A} cm · B:{' '}
                {pendingAddProduct.medidas.B} cm · C:{' '}
                {pendingAddProduct.medidas.C} cm
              </dd>
            </div>
          </dl>
          <div className="reemplazantes-add-material-actions">
            <Button
              onClick={() => {
                setPendingAddProduct(null)
                setAddMaterialError(null)
              }}
            >
              Volver
            </Button>
            <Button type="primary" onClick={handleConfirm}>
              Confirmar
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}

export const ProductCardsGrid = ({
  products,
  referenceProduct,
  selectedIds,
  matchedIds,
  matchedPairs,
  onCardClick,
  onDeleteCustom,
  dimmed,
  highlightMatched = true,
}) => (
  <div
    className={`reemplazantes-cards-grid${
      dimmed ? ' reemplazantes-cards-grid--dimmed' : ''
    }`}
  >
    {referenceProduct && (
      <ProductCard product={referenceProduct} isReference index={0} />
    )}
    {products.map((product, mapIndex) => {
      const isCustom = Boolean(product.isCustom)
      const isMatched = matchedPairs
        ? isProductInAnyMatch(product.id, matchedPairs)
        : (matchedIds ?? []).includes(product.id)
      const canDelete = isCustom && !isMatched
      const isSelected = Array.isArray(selectedIds)
        ? selectedIds.includes(product.id)
        : selectedIds === product.id

      return (
        <div key={product.id} className="reemplazantes-product-card-wrapper">
          {canDelete && (
            <button
              type="button"
              className="reemplazantes-product-card-delete"
              aria-label={`Eliminar ${product.nombre}`}
              onClick={() => onDeleteCustom(product.id)}
            >
              ×
            </button>
          )}
          <ProductCard
            product={product}
            index={referenceProduct ? mapIndex + 1 : mapIndex}
            isSelected={isSelected}
            isMatched={highlightMatched && isMatched}
            isCustom={isCustom}
            onClick={() => onCardClick(product)}
          />
        </div>
      )
    })}
  </div>
)
